import { logCorrelationId } from '@brighthub/structured-logging';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Draggable, Droppable } from 'primeng/dragdrop';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Textarea } from 'primeng/textarea';
import { ConfirmationService } from 'primeng/api';
import { CARE_PLAN_MAX_ACTIVE_GOALS } from '../../care-plan.model';
import {
  CarePlanStore,
  GoalSaveError,
  LiveGoalWrite,
} from '../../care-plan.store';
import { CarePlanAnalytics } from '../../analytics/care-plan-analytics';

export interface ManageGoalsDialogData {
  store: CarePlanStore;
}

type GoalMark = 'complete' | 'remove' | null;

interface WorkingGoal {
  id: string;
  displayText: string;
  originalDescription: string | null;
  linkedCaseCount: number;
  mark: GoalMark;
}

/**
 * Client goals modal: active-only list (sortOrder), add/edit/complete/
 * remove/reorder staged locally; live Save uses individual requests. Cancel
 * (or mask, via `tryClose`) discards (D18). Reorder is drag-and-drop from the
 * row handle, with arrow keys on the same handle as the keyboard path.
 */
@Component({
  selector: 'bn-manage-goals-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, Button, ConfirmDialog, Draggable, Droppable, Textarea],
  providers: [ConfirmationService],
  templateUrl: './manage-goals-dialog.component.html',
  styleUrl: './manage-goals-dialog.component.scss',
})
export class ManageGoalsDialogComponent {
  private readonly _goalLogKey = logCorrelationId();
  private readonly _ref = inject(DynamicDialogRef);
  private readonly _config = inject(DynamicDialogConfig);
  private readonly _confirmation = inject(ConfirmationService);
  private readonly _analytics = inject(CarePlanAnalytics);
  private readonly _data = this._config.data as ManageGoalsDialogData;

  protected readonly store = this._data.store;
  constructor() {
    effect(() => {
      if (!this.store.goalsEnabled()) this._ref.close();
    });
  }
  protected readonly maxActiveGoals = CARE_PLAN_MAX_ACTIVE_GOALS;

  private readonly _initial: WorkingGoal[] = this.store
    .activeGoals()
    .map((g) => ({
      id: g.id,
      displayText: g.displayText,
      originalDescription: g.originalDescription,
      linkedCaseCount: g.linkedCaseIds.length,
      mark: null,
    }));

  readonly workingGoals = signal<WorkingGoal[]>(
    this._initial.map((g) => ({ ...g })),
  );
  readonly draftGoals = signal<string[]>([]);
  readonly editingGoalId = signal<string | null>(null);
  readonly editDraft = signal('');
  readonly draggingIndex = signal<number | null>(null);
  readonly saving = signal(false);
  readonly saveError = signal<string | null>(null);
  readonly liveSaveStarted = signal(false);
  readonly ambiguousCreate = signal(false);
  private remainingWrites: LiveGoalWrite[] | null = null;
  private createdIds: string[] = [];
  private readonly openingGuid = this.store.sessionGuid();

  protected readonly survivingCount = computed(
    () => this.workingGoals().filter((g) => g.mark === null).length,
  );

  protected readonly atCap = computed(
    () =>
      this.survivingCount() + this.draftGoals().length >= this.maxActiveGoals,
  );

  protected readonly dirty = computed(() => {
    if (this.draftGoals().some((text) => text.trim().length > 0)) return true;
    const working = this.workingGoals();
    if (working.some((g) => g.mark !== null)) return true;
    return working.some((g, index) => {
      const original = this._initial[index];
      return (
        !original ||
        original.id !== g.id ||
        original.displayText !== g.displayText
      );
    });
  });

  startEdit(goal: WorkingGoal): void {
    this.editingGoalId.set(goal.id);
    this.editDraft.set(goal.displayText);
  }

  commitEdit(goalId: string): void {
    const text = this.editDraft().trim();
    if (text.length > 0 && text.length <= 200) {
      this.workingGoals.update((goals) =>
        goals.map((g) => (g.id === goalId ? { ...g, displayText: text } : g)),
      );
    }
    this.editingGoalId.set(null);
  }

  cancelEdit(): void {
    this.editingGoalId.set(null);
    this.editDraft.set('');
  }

  markComplete(goalId: string): void {
    this.workingGoals.update((goals) =>
      goals.map((g) => (g.id === goalId ? { ...g, mark: 'complete' } : g)),
    );
  }

  markRemove(goalId: string): void {
    this.workingGoals.update((goals) =>
      goals.map((g) => (g.id === goalId ? { ...g, mark: 'remove' } : g)),
    );
  }

  moveUp(index: number): void {
    if (this.saving() || this.liveSaveStarted()) return;
    if (index <= 0) return;
    this.workingGoals.update((goals) => {
      const next = [...goals];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
  }

  moveDown(index: number): void {
    if (this.saving() || this.liveSaveStarted()) return;
    this.workingGoals.update((goals) => {
      if (index >= goals.length - 1) return goals;
      const next = [...goals];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next;
    });
  }

  onDragStart(index: number): void {
    if (this.saving() || this.liveSaveStarted()) return;
    this.draggingIndex.set(index);
  }

  onDragEnd(): void {
    this.draggingIndex.set(null);
  }

  onDrop(targetIndex: number): void {
    if (this.saving() || this.liveSaveStarted()) return;
    const from = this.draggingIndex();
    this.draggingIndex.set(null);
    if (from === null || from === targetIndex) return;
    this.workingGoals.update((goals) => {
      const next = [...goals];
      const [moved] = next.splice(from, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
  }

  addDraftRow(): void {
    if (this.atCap()) {
      this._analytics.goalCapBlocked(this.openingGuid);
      return;
    }
    this.draftGoals.update((rows) => [...rows, '']);
  }

  setDraftText(index: number, text: string): void {
    this.draftGoals.update((rows) =>
      rows.map((r, i) => (i === index ? text : r)),
    );
  }

  removeDraftRow(index: number): void {
    this.draftGoals.update((rows) => rows.filter((_, i) => i !== index));
  }

  save(): void {
    if (!this.store.goalsEnabled() || this.saving() || this.ambiguousCreate())
      return;
    if (this.store.isLive()) {
      void this.saveLive();
      return;
    }
    const survivingIds: string[] = [];
    for (const goal of this.workingGoals()) {
      if (goal.mark === 'complete') {
        this.store.completeGoal(goal.id);
        this._analytics.goalCompleted(
          goal.id,
          goal.linkedCaseCount,
          this.openingGuid,
        );
      } else if (goal.mark === 'remove') {
        this.store.removeGoal(goal.id);
        this._analytics.goalRemoved(
          goal.id,
          goal.linkedCaseCount,
          this.openingGuid,
        );
      } else {
        this.store.editGoal(goal.id, goal.displayText);
        const original = this._initial.find((g) => g.id === goal.id);
        if (original && original.displayText !== goal.displayText) {
          this._analytics.goalEdited(goal.id, this.openingGuid);
        }
        survivingIds.push(goal.id);
      }
    }
    for (const text of this.draftGoals()) {
      const trimmed = text.trim();
      if (trimmed.length === 0) continue;
      const created = this.store.addGoal(trimmed);
      if (created) {
        survivingIds.push(created.id);
        this._analytics.goalCreated(
          created.id,
          this.store.activeGoals().length,
          this.openingGuid,
        );
      }
    }
    this.store.reorderGoals(survivingIds);
    this._ref.close({ saved: true });
  }

  private async saveLive(): Promise<void> {
    if (this.openingGuid !== this.store.sessionGuid() || this.editingGoalId())
      return;
    if (!this.remainingWrites) {
      const updates: LiveGoalWrite[] = [];
      for (const goal of this.workingGoals()) {
        if (goal.mark)
          updates.push({
            kind: 'update',
            goalId: goal.id,
            statusCode:
              goal.mark === 'complete' ? 'COMPLETED' : 'NO_LONGER_RELEVANT',
          });
        else if (
          goal.displayText !==
          this._initial.find((item) => item.id === goal.id)?.displayText
        ) {
          updates.push({
            kind: 'update',
            goalId: goal.id,
            description: goal.displayText,
          });
        }
      }
      this.remainingWrites = [
        ...updates.filter(
          (change) => change.kind === 'update' && change.statusCode,
        ),
        ...updates.filter(
          (change) => change.kind === 'update' && !change.statusCode,
        ),
        ...this.draftGoals()
          .filter((text) => text.trim())
          .map((description) => ({ kind: 'create' as const, description })),
      ];
    }
    this.saving.set(true);
    this.saveError.set(null);
    this.liveSaveStarted.set(true);
    try {
      await this.store.saveLiveGoals(
        [...this.remainingWrites],
        (change, id) => {
          this.remainingWrites?.shift();
          if (change.kind === 'create' && id) {
            this.createdIds.push(id);
          } else if (change.kind === 'update') {
            const count =
              this._initial.find((goal) => goal.id === change.goalId)
                ?.linkedCaseCount ?? 0;
            if (change.statusCode === 'COMPLETED')
              this._analytics.goalCompleted(
                change.goalId,
                count,
                this.openingGuid,
              );
            else if (change.statusCode === 'NO_LONGER_RELEVANT')
              this._analytics.goalRemoved(
                change.goalId,
                count,
                this.openingGuid,
              );
            else this._analytics.goalEdited(change.goalId, this.openingGuid);
          }
        },
        true,
        this._goalLogKey,
      );
      this.store.reorderGoals([
        ...this.workingGoals()
          .filter((goal) => !goal.mark)
          .map((goal) => goal.id),
        ...this.createdIds,
      ]);
      this._ref.close({ saved: true });
    } catch (error) {
      this.ambiguousCreate.set(
        error instanceof GoalSaveError && error.ambiguousCreate,
      );
      this.saveError.set(
        (error instanceof Error ? error.message : 'Goals could not be saved.') +
          ' Earlier successful changes are retained.',
      );
    } finally {
      this.saving.set(false);
    }
  }

  tryClose(): void {
    if (this.saving()) return;
    if (!this.dirty()) {
      this._ref.close();
      return;
    }
    this._confirmation.confirm({
      message:
        'You have unsaved changes — are you sure you want to discard them?',
      header: 'Unsaved changes',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Discard',
      rejectLabel: 'Stay',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this._ref.close(),
    });
  }
}
